import { useEffect, useState } from 'react'
import { api, type AwaitingMapping, type DateSource, type DetectedShape } from '../api'
import { RowMapping, type Spec } from './RowMapping'

/**
 * Mapping a waiting shape, and then reading what was waiting on it — §8.3.
 *
 * **The screen that was missing.** `layouts.confirm` and `mailbox.replay` have
 * existed since §8.3 shipped, and nothing in the application called either — so
 * Unmapped layouts could tell you a client's guidelines had arrived and were
 * unreadable, and offered no way to make them readable. The queue was a notice
 * board.
 *
 * **One confirmation, then never again.** That is §8.3's whole economy: the
 * first document in a layout stops and asks where the table is, and every
 * document in that layout afterwards is read deterministically with no model
 * involved. So this is deliberately not a wizard — it is one question about one
 * document, answered once.
 *
 * **Confirming and processing are two buttons, not one.** They are different
 * decisions: the mapping may be right and the backlog may still want looking at
 * before it is read. `mailbox.replay` makes the same separation in the store,
 * and collapsing it here would take the choice away on the screen while leaving
 * it in the code.
 *
 * Nothing this produces binds anything. Every value read becomes a *proposal*
 * an approver decides (§8.6), so the button clears one queue into another
 * rather than into force — and it says so, because "process" reads like
 * something taking effect.
 */
export function ShapeMapper({
  shape,
  onDone,
}: {
  shape: AwaitingMapping
  onDone: () => void
}) {
  const [detected, setDetected] = useState<DetectedShape | null>(null)
  const [name, setName] = useState('')
  const [rowMap, setRowMap] = useState<Record<string, Spec>>({})
  // Where the effective date comes from. Deliberately starts unanswered rather
  // than on a default: a layout confirmed without this refuses every document
  // that ever arrives in it, and the commonest way to get that was a screen
  // that never mentioned the date at all.
  const [dateSource, setDateSource] = useState<'' | DateSource>('')
  const [dateCell, setDateCell] = useState('')
  // The day the waiting documents take effect, where the layout carries no date
  // and a person gives each one's (decision 70).
  const [effectiveOn, setEffectiveOn] = useState('')
  const [busy, setBusy] = useState(false)
  const [saved, setSaved] = useState(false)
  const [result, setResult] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setError(null)
    api
      .submissionShape(shape.example_submission_id)
      .then((d) => {
        setDetected(d)
        // A profile already exists for this shape when somebody confirmed it
        // through the import wizard. Loading it means this screen edits what
        // is there rather than silently proposing a competing mapping.
        if (d.known_profile) {
          setName(d.known_profile.name)
          setRowMap(d.known_profile.row_map as Record<string, Spec>)
          setDateSource(d.known_profile.effective_date_source ?? 'cell')
          setDateCell(d.known_profile.effective_date_cell ?? '')
          setSaved(true)
        } else {
          setName(shape.example_filename ?? 'Client layout')
        }
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
  }, [shape.example_submission_id, shape.example_filename])

  /** The first column of the sheet — every row the document holds. */
  const labels = (detected?.rows ?? [])
    .map((row) => row[0] ?? '')
    .filter((label) => label.trim() !== '')

  /** Rows given a meaning. None means every row would be kept as words, which is
   *  never what a limits table wants, so the mapping is not saved that way. */
  const meaningful = Object.keys(rowMap).length

  /** Everything the layout needs before it can read a document without asking. */
  const answered = name.trim() !== '' && dateSource !== '' &&
    (dateSource !== 'cell' || dateCell.trim() !== '') && meaningful > 0

  async function confirm() {
    if (!detected || !answered) return
    setBusy(true)
    setError(null)
    try {
      await api.confirmLayout({
        name: name.trim(),
        fingerprint: detected.fingerprint,
        sheet_name: detected.shape.header_row >= 0 ? detected.sheet : null,
        header_row: detected.shape.header_row,
        label_column: detected.shape.label_column,
        portfolio_codes: detected.shape.portfolio_codes,
        matched: detected.shape.matched,
        row_map: rowMap,
        effective_date_source: dateSource,
        effective_date_cell: dateSource === 'cell' ? dateCell.trim() : null,
      })
      setSaved(true)
      // Says what has *not* happened yet, and points at the control that does
      // it. The old message ended on "this confirmation was the one time",
      // which reads as finished — and the shape stays in this queue until the
      // backlog is read, so the screen then looked like the save had failed.
      // It was pressed four times in a row before anybody said so.
      setResult(
        shape.submissions === 0
          ? 'Mapping saved. Every future document in this shape is read with it.'
          : `Mapping saved. Every future document in this shape is read without ` +
              `asking again. The ${shape.submissions} document(s) already waiting ` +
              'have not been read yet — that is the next button, and this shape ' +
              'stays in this queue until you press it.',
      )
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  async function process(withDate = true) {
    setBusy(true)
    setError(null)
    try {
      const perDocument = dateSource === 'document'
      const outcome = await api.replayShape(
        shape.fingerprint,
        perDocument && withDate ? effectiveOn : undefined,
      )
      if (perDocument && !withDate) {
        setResult(
          `${outcome.read} document(s) moved to Waiting for a date, where each ` +
            'is read once you give the day it takes effect.',
        )
        onDone()
        return
      }
      setResult(
        `Read ${outcome.read} submission(s) — ${outcome.proposals} proposal(s) ` +
          'now waiting on Approvals. Nothing is in force: an approver decides ' +
          'each one. The client’s file is on Mandate documents as a ' +
          'guideline pack and every proposal cites it — which is where the ' +
          'numbers came from, not the contract that justifies them. Clause ' +
          'review is where they get that. Anything that could not be read is ' +
          'on Refused items with its reason.',
      )
      onDone()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  if (error && !detected) {
    return (
      <p className="error" role="alert">
        {error}
      </p>
    )
  }
  if (!detected) return <p className="note">Reading the document…</p>

  return (
    <div className="shape-mapper" data-testid="shape-mapper">
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {result && <p className="done">{result}</p>}

      <p className="note">
        {detected.shape.reason}. The table starts at row{' '}
        <strong>{detected.shape.header_row + 1}</strong>, and{' '}
        <strong>{detected.shape.matched}</strong> of its column headers are funds
        already in the system
        {detected.shape.unmatched_portfolio_codes.length > 0 && (
          <>
            {' '}
            — <strong>{detected.shape.unmatched_portfolio_codes.join(', ')}</strong>{' '}
            are not, so nothing will be staged for them. Add them under{' '}
            <em>Add a fund</em> first if that is a typo rather than a fund you do
            not hold.
          </>
        )}
        .
      </p>

      <label className="stacked">
        Call this layout
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Braeton monthly guidelines"
          title="How somebody recognises this shape next year, when the client's file arrives again and is read without a word."
        />
      </label>

      {/* Where the effective date comes from — §5.2's business time. Asked
          here, once, because it is a fact about the *layout*: this client
          either puts a date in their file or they do not, and the answer holds
          for every document they ever send in this shape.

          There is no default and no pre-selected option. Either answer is
          reasonable and only the client's file says which, so a default would
          be this screen guessing at the one thing the whole refusal exists to
          stop it guessing. */}
      <fieldset className="stacked">
        <legend>When do these guidelines take effect?</legend>
        <label className="choice">
          <input
            type="radio"
            name="effective-date-source"
            checked={dateSource === 'cell'}
            onChange={() => setDateSource('cell')}
          />
          The date is in the document, at cell
          <input
            className="cell"
            value={dateCell}
            disabled={dateSource !== 'cell'}
            onChange={(e) => setDateCell(e.target.value)}
            placeholder="B2"
            title="The cell reference as it appears in the client's own file — B2, D1."
          />
        </label>
        <label className="choice">
          <input
            type="radio"
            name="effective-date-source"
            checked={dateSource === 'arrival'}
            onChange={() => setDateSource('arrival')}
          />
          This layout carries no date — its documents take effect on the day
          they arrive
        </label>
        <label className="choice">
          <input
            type="radio"
            name="effective-date-source"
            checked={dateSource === 'document'}
            onChange={() => setDateSource('document')}
          />
          No date in the file — I give each document’s date
        </label>
        {dateSource === 'document' && (
          <p className="note">
            Each document in this shape waits under <strong>Waiting for a date</strong>{' '}
            until someone gives the day it takes effect, then it is read.
          </p>
        )}
        {dateSource === 'arrival' && (
          <p className="warn">
            Every document in this shape will be dated when it arrives. If this
            client ever sends guidelines ahead of time — limits agreed now that
            start next quarter — they would bind from the day the mail landed,
            which is early. Choose the cell instead if their file carries a date.
          </p>
        )}
      </fieldset>

      {/* What each row of the document means, against the customer's own
          dictionary. Suggested, never decided: §8.3's economy is one
          confirmation by a person, not an inference nobody checked. */}
      <RowMapping labels={labels} value={rowMap} onChange={setRowMap} />

      <p className={meaningful === 0 ? 'warn' : 'note'} data-testid="rows-with-meaning">
        {meaningful === 0
          ? 'No row has a meaning yet, so every row would be kept as words and nothing could be judged. Choose what the rows mean above, or Suggest again.'
          : `${meaningful} of ${labels.length} rows have a meaning. The rest are kept as words.`}
      </p>

      <div className="actions">
        <button
          disabled={busy || !answered}
          title={
            answered
              ? undefined
              : meaningful === 0
                ? 'Give at least one row a meaning.'
                : 'Name the layout, and say where its effective date comes from.'
          }
          onClick={() => void confirm()}
        >
          {saved ? 'Save the mapping again' : 'Confirm this mapping'}
        </button>
        {/* Not `ghost` once the mapping is saved: at that point this is the
            step the reader is on, and a secondary-looking control beside a
            just-completed primary one reads as optional. Absent when the
            mapper edits a layout nothing is waiting on. */}
        {shape.submissions > 0 && (<>
        <button
          className={saved ? undefined : 'ghost'}
          title={
            saved
              ? undefined
              : 'Confirm the mapping first — there is nothing to read the documents with.'
          }
          onClick={() => void process()}
          disabled={busy || !saved || (dateSource === 'document' && !effectiveOn)}
        >
          Read the {shape.submissions} waiting submission(s)
        </button>
        {saved && dateSource === 'document' && (
          <>
            <label className="inline">
              taking effect on{' '}
              <input
                type="date"
                value={effectiveOn}
                onChange={(e) => setEffectiveOn(e.target.value)}
                data-testid="replay-effective-on"
              />
            </label>
            <button className="ghost" disabled={busy} onClick={() => void process(false)}>
              Give each its own date
            </button>
          </>
        )}
        </>)}
      </div>
      {shape.submissions > 0 && (
        <p className="note">
          Reading them produces <strong>proposals</strong>, not limits. Every value
          goes to an approver, whoever sent it and however cleanly it read.
        </p>
      )}
    </div>
  )
}
